import React, { useCallback, useEffect, useState } from "react";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";

import { api } from "./api";
import { Bar, Btn, Panel, Strip } from "./ui";

const ESTADOS = [
  { k: "proposed", t: "pendientes" },
  { k: "approved", t: "aprobadas" },
  { k: "applied", t: "aplicadas" },
  { k: "rejected", t: "rechazadas" },
  { k: "failed", t: "fallidas" },
  { k: "all", t: "todas" },
];

const TONO = { proposed: "warn", applied: "ok", failed: "error", rejected: "info", approved: "info" };

/**
 * Cola de aprobación: el paso obligatorio entre "el sistema propuso" y "el sistema lo hizo".
 *
 * Aprobar acá NO ejecuta el cambio: lo habilita. La ejecución la hace `php artisan meta:apply`,
 * que no corre solo, y que además chequea que nadie haya tocado el objeto entretanto.
 */
export default function Aprobaciones() {
  const [estado, setEstado] = useState("proposed");
  const [acciones, setAcciones] = useState(null);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [quien, setQuien] = useState("");
  const [nota, setNota] = useState("");
  const [abierta, setAbierta] = useState(null);

  const cargar = useCallback((e) => {
    setAcciones(null);
    setError(null);
    api.listarAcciones(e).then((r) => setAcciones(r.acciones)).catch((x) => setError(x.message));
  }, []);

  useEffect(() => cargar(estado), [estado, cargar]);

  async function decidir(id, aprobar) {
    if (!quien.trim()) {
      setError("Poné tu nombre: toda aprobación queda registrada con su responsable.");
      return;
    }
    setError(null);
    try {
      const r = await api.decidirAccion(id, { por: quien, aprobar, nota: nota || undefined });
      setMensaje(r.nota);
      setNota("");
      cargar(estado);
    } catch (e) {
      setError(e.message);
    }
  }

  return (
    <div>
      <Bar>
        {ESTADOS.map((e) => (
          <Btn key={e.k} on={estado === e.k} onClick={() => setEstado(e.k)}>{e.t}</Btn>
        ))}
        <div style={{ flex: 1 }} />
        <TextField placeholder="tu nombre" value={quien} onChange={(e) => setQuien(e.target.value)} style={{ width: 150 }} />
      </Bar>

      {error && <Strip tone="error">{error}</Strip>}
      {mensaje && <Strip tone="ok">{mensaje}</Strip>}

      {!acciones && <CircularProgress size={22} />}

      {acciones && acciones.length === 0 && (
        <Strip tone="info">No hay acciones en este estado.</Strip>
      )}

      {acciones && acciones.map((a) => (
        <Panel
          key={a.id}
          title={`#${a.id} · ${a.kind}`}
          right={
            <>
              <span style={{ fontSize: 10.5, fontWeight: 800, color: TONO[a.status] === "ok" ? "#16A34A" : TONO[a.status] === "error" ? "#D92D20" : "#CA8A04" }}>
                {a.status}
              </span>
              <Btn onClick={() => setAbierta(abierta === a.id ? null : a.id)}>
                {abierta === a.id ? "ocultar payload" : "payload"}
              </Btn>
            </>
          }
          pad={10}
        >
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{a.summary}</div>
          <div style={{ fontSize: 11.5, color: "#7A8699" }}>
            {a.target_type} {a.target_id} · {a.target_name} · propuesta por {a.proposed_by}
            {a.expires_at ? ` · vence ${String(a.expires_at).substring(0, 10)}` : ""}
            {a.reviewed_by ? ` · revisada por ${a.reviewed_by}${a.review_note ? ` (${a.review_note})` : ""}` : ""}
          </div>

          {a.reason && (
            <div style={{ fontSize: 12, whiteSpace: "pre-wrap", marginTop: 6, paddingLeft: 8, borderLeft: "2px solid #E3E8F0", color: "#46536B" }}>
              {a.reason}
            </div>
          )}

          {a.error && <Strip tone="error" style={{ marginTop: 6, marginBottom: 0 }}><b>error:</b> {a.error}</Strip>}

          {abierta === a.id && (
            <pre style={{ background: "#0F172A", color: "#DCE3ED", padding: 10, borderRadius: 6, fontSize: 11, overflowX: "auto", marginTop: 8, marginBottom: 0 }}>
              {JSON.stringify(a.payload, null, 2)}
            </pre>
          )}

          {a.status === "proposed" && (
            <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <TextField placeholder="nota (opcional)" value={nota} onChange={(e) => setNota(e.target.value)} style={{ width: 220 }} />
              <Btn tone="ok" onClick={() => decidir(a.id, true)}>aprobar</Btn>
              <Btn tone="no" onClick={() => decidir(a.id, false)}>rechazar</Btn>
              <span style={{ fontSize: 11.5, color: "#7A8699" }}>
                Aprobar no ejecuta: habilita. Después hay que correr <code>meta:apply</code>.
              </span>
            </div>
          )}
        </Panel>
      ))}
    </div>
  );
}
